import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Trash2, Mail } from "lucide-react";
import { statusColor, engagementColor, engagementOf, relationshipOf, relationshipBadge } from "./accountsModel";
import { AccountKnowledgePanel } from "./AccountKnowledgePanel";

const Stat = ({ label, children, className = "text-white" }) => (
  <div className="glass-card-light rounded-lg p-4">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`text-base ${className}`}>{children}</p>
  </div>
);

export const AccountDetail = ({ account, onBack, onDelete, onChanged }) => {
  const persona = account.persona || account.targetPersona;
  const engagement = engagementOf(account);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="account-detail">
      <div className="flex items-center justify-between">
        <Button onClick={onBack} variant="ghost" className="text-gray-400 hover:text-white hover:bg-white/5" data-testid="back-to-accounts-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Accounts
        </Button>
        <Button onClick={onDelete} variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid="delete-account-detail-btn">
          <Trash2 className="h-4 w-4 mr-2" /> Delete Account
        </Button>
      </div>

      <Card className="glass-card rounded-xl p-8">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-2xl text-white mb-2" data-testid="account-detail-name">{account.companyName}</h2>
            <p className="text-gray-500">{account.industry}{account.website ? ` · ${account.website}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${relationshipBadge(relationshipOf(account))} border text-xs uppercase tracking-wide`} data-testid="account-detail-relationship">{relationshipOf(account)}</Badge>
            <Badge className={`${statusColor(account.status)} border text-sm`}>{account.status}</Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <Stat label="Target Persona">{persona}</Stat>
          <Stat label="Engagement Score" className={engagementColor(engagement)}>{engagement}/100</Stat>
          <Stat label="Last Contact">{account.lastContact ? new Date(account.lastContact).toLocaleDateString() : "Never"}</Stat>
          <Stat label="Next Action" className="text-ism-teal/80">{account.nextAction || "TBD"}</Stat>
        </div>

        <div className="border-t border-white/10 pt-6">
          <AccountKnowledgePanel accountId={account.accountId} onChanged={onChanged} />
        </div>

        <div className="flex gap-3 mt-6 flex-wrap">
          <Button className="bg-gradient-to-r from-ism-teal to-ism-teal-light hover:from-ism-teal-light hover:to-ism-teal text-white" data-testid="account-generate-outreach-btn">
            <Mail className="h-4 w-4 mr-2" /> Generate Outreach
          </Button>
          <Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5" data-testid="account-update-status-btn">Update Status</Button>
        </div>
      </Card>
    </motion.div>
  );
};
